import { useState, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CreateOrderSchema, createOrderSchema } from "@/api.schemas";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { PhoneInputShadcn } from "@/components/ui/phone-input-shadcn";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, PlusCircle, Check } from "lucide-react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useSession } from "@/services/auth0/session";
import { Skeleton } from "@/components/ui/skeleton";
import { ShipmentMap } from "./shipment-map";
import { usePricingOptions } from "@/api/use-order-pricing-options";
import { logger } from "@/services/logger";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ParsedOrder {
  senderName: string;
  senderPhone: string;
  senderAddress: string;
  recipientName: string;
  recipientPhone: string;
  recipientAddress: string;
  pickupNotes?: string;
  deliveryNotes?: string;
  parcelsCount: number;
}

// Define a type for the validated address structure using the inferred schema
type ValidatedAddress = z.infer<typeof createOrderSchema.shape.pickupAddress>;

interface BulkCreateOrderReviewFormProps {
  order: ParsedOrder;
  isCompleted: boolean;
  onSubmit: (data: CreateOrderSchema) => Promise<void>;
  onFormReady?: (submit: () => void) => void;
}

export function BulkCreateOrderReviewForm({
  order,
  isCompleted,
  onSubmit,
  onFormReady,
}: BulkCreateOrderReviewFormProps) {
  const { customer } = useSession();
  const places = useMapsLibrary("places");
  const geocoder = useMapsLibrary("geocoding");
  const [isLoading, setIsLoading] = useState(true);
  const [showAddressConfirmDialog, setShowAddressConfirmDialog] =
    useState(false);
  const [validatedAddresses, setValidatedAddresses] = useState<{
    pickup: ValidatedAddress;
    delivery: ValidatedAddress;
  } | null>(null);

  const form = useForm<CreateOrderSchema>({
    resolver: zodResolver(createOrderSchema),
    defaultValues: {
      type: "STANDARD",
      status: "PENDING",
      customerType: "INDIVIDUAL",
      senderCustomerId: customer?.id,
      senderName: order.senderName,
      senderPhone: order.senderPhone?.startsWith("+")
        ? order.senderPhone
        : `+1${order.senderPhone}`,
      recipientName: order.recipientName,
      recipientPhone: order.recipientPhone?.startsWith("+")
        ? order.recipientPhone
        : `+1${order.recipientPhone}`,
      pickupNotes: order.pickupNotes,
      deliveryNotes: order.deliveryNotes,
      pickupAddress: {
        line1: order.senderAddress,
        line2: "",
        city: "",
        province: "",
        postalCode: "",
        country: "CA",
        location: { lat: 0, lng: 0 },
      },
      deliveryAddress: {
        line1: order.recipientAddress,
        line2: "",
        city: "",
        province: "",
        postalCode: "",
        country: "CA",
        location: { lat: 0, lng: 0 },
      },
      parcels: Array(order.parcelsCount).fill({
        weight: 2,
        dimensions: { length: 30, width: 30, height: 30 },
        isFragile: false,
        requiresCooling: false,
      }),
    },
  });

  const watchedValues = useWatch({
    control: form.control,
    name: ["pickupAddress", "deliveryAddress", "parcels"],
  });

  const pricingInputs = useMemo(
    () => ({
      pickupAddress: watchedValues[0],
      deliveryAddress: watchedValues[1],
      parcels: watchedValues[2]?.map((parcel) => ({
        weight: Number(parcel.weight) || 0,
        dimensions: {
          length: Number(parcel.dimensions?.length) || 0,
          width: Number(parcel.dimensions?.width) || 0,
          height: Number(parcel.dimensions?.height) || 0,
        },
        isFragile: Boolean(parcel.isFragile),
      })),
    }),
    [watchedValues]
  );

  const {
    pricingOptions,
    pricingOptionMetadata,
    error: shippingOptionsError,
    isLoading: isPricingLoading,
  } = usePricingOptions(pricingInputs);

  // Set first pricing option when options become available
  useEffect(() => {
    if (pricingOptions.length > 0) {
      form.setValue(
        "type",
        pricingOptions[0].speedType as
          | "STANDARD"
          | "EXPRESS"
          | "SCHEDULED"
          | "SAME_DAY"
      );
    }
  }, [pricingOptions, form]);

  // Extract address components from Google geocoding result
  const extractAddressComponents = (
    place: google.maps.GeocoderResult
  ): ValidatedAddress => {
    const streetNumber =
      place.address_components?.find((c) => c.types.includes("street_number"))
        ?.long_name ?? "";

    const route =
      place.address_components?.find((c) => c.types.includes("route"))
        ?.long_name ?? "";

    // Try to extract subpremise (apt/unit number) for line2
    const subpremise =
      place.address_components?.find((c) => c.types.includes("subpremise"))
        ?.long_name ?? "";

    // Construct line2 from subpremise if available
    const line2 = subpremise ? `Unit ${subpremise}` : "";

    // Construct line1
    const line1 =
      streetNumber && route
        ? `${streetNumber} ${route}`
        : place.formatted_address || "";

    return {
      line1,
      line2,
      city:
        place.address_components?.find((c) => c.types.includes("locality"))
          ?.long_name ?? "",
      province:
        place.address_components?.find((c) =>
          c.types.includes("administrative_area_level_1")
        )?.long_name ?? "",
      postalCode:
        place.address_components?.find((c) => c.types.includes("postal_code"))
          ?.long_name ?? "",
      country: "CA",
      location: {
        lat: place.geometry?.location?.lat() ?? 0,
        lng: place.geometry?.location?.lng() ?? 0,
      },
    };
  };

  // Validate addresses using Google Places API
  useEffect(() => {
    if (!places || !geocoder) return;

    const validateAddresses = async () => {
      setIsLoading(true);
      try {
        const geocoderInstance = new google.maps.Geocoder();

        const [pickupResult, deliveryResult] = await Promise.all([
          new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
            void geocoderInstance.geocode(
              {
                address: order.senderAddress,
                language: "en",
                region: "CA",
              },
              (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results) {
                  resolve(results);
                } else {
                  reject(new Error(`Geocoding failed: ${status}`));
                }
              }
            );
          }),
          new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
            void geocoderInstance.geocode(
              {
                address: order.recipientAddress,
                language: "en",
                region: "CA",
              },
              (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results) {
                  resolve(results);
                } else {
                  reject(new Error(`Geocoding failed: ${status}`));
                }
              }
            );
          }),
        ]);

        if (pickupResult[0] && deliveryResult[0]) {
          const pickupAddress = extractAddressComponents(pickupResult[0]);
          const deliveryAddress = extractAddressComponents(deliveryResult[0]);

          setValidatedAddresses({
            pickup: pickupAddress,
            delivery: deliveryAddress,
          });
          setShowAddressConfirmDialog(true);
        } else {
          logger.warn("Geocoding failed for one or both addresses.");
        }
      } catch (error) {
        logger.error("Error validating addresses:", error);
      } finally {
        setIsLoading(false);
      }
    };

    void validateAddresses();
  }, [places, geocoder, order, form]);

  // Handle address confirmation
  const confirmAddresses = () => {
    if (validatedAddresses) {
      form.setValue("pickupAddress", validatedAddresses.pickup);
      form.setValue("deliveryAddress", validatedAddresses.delivery);
    }
    setShowAddressConfirmDialog(false);
  };

  // Skip address confirmation
  const skipAddressConfirmation = () => {
    setShowAddressConfirmDialog(false);
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    const selectedOption = pricingOptions.find(
      (option) => option.speedType === values.type
    );

    if (!selectedOption || !pricingOptionMetadata) {
      logger.error("No shipping option selected or missing metadata");
      return;
    }

    const formattedParcels = values.parcels.map((parcel) => ({
      weight: Number(parcel.weight) || 0,
      dimensions: {
        length: Number(parcel.dimensions?.length) || 0,
        width: Number(parcel.dimensions?.width) || 0,
        height: Number(parcel.dimensions?.height) || 0,
      },
      isFragile: Boolean(parcel.isFragile),
      requiresCooling: Boolean(parcel.requiresCooling),
    }));

    const submitData = {
      ...values,
      placerCustomerId: customer?.id,
      pickupDepotId: pricingOptionMetadata?.pickupServiceArea?.depotId,
      deliveryDepotId: pricingOptionMetadata?.deliveryServiceArea?.depotId,
      parcels: formattedParcels,
    };

    await onSubmit(submitData);
  });

  useEffect(() => {
    if (onFormReady) {
      onFormReady(() => handleSubmit());
    }
  }, [onFormReady, handleSubmit]);

  if (isLoading) {
    // Enhanced loading state with skeletons
    return (
      <div className="space-y-6 px-6">
        {/* Skeleton for Header */}
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-1/4" />
        </div>

        {/* Skeleton for Sender/Recipient Cards */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-1/3" />
            </CardHeader>
            <CardContent className="space-y-8">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-1/3" />
            </CardHeader>
            <CardContent className="space-y-8">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        </div>

        {/* Skeleton for Parcels Card */}
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-1/4" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-12 w-full" />
          </CardContent>
          <CardFooter>
            <Skeleton className="h-8 w-1/5" />
          </CardFooter>
        </Card>

        {/* Skeleton for Map and Pricing Options */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="h-[600px]">
            <CardContent className="p-0 h-full">
              <Skeleton className="w-full h-full rounded-lg" />
            </CardContent>
          </Card>
          <Card className="h-full">
            <CardHeader>
              <Skeleton className="h-6 w-1/3" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <>
      <AlertDialog
        open={showAddressConfirmDialog}
        onOpenChange={setShowAddressConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Addresses</AlertDialogTitle>
            <AlertDialogDescription>
              We've validated your addresses. Please confirm they are correct:
              <div className="mt-4 space-y-4">
                <div>
                  <h4 className="font-medium">Pickup Address:</h4>
                  <p>{validatedAddresses?.pickup.line1}</p>
                  {validatedAddresses?.pickup.line2 && (
                    <p>{validatedAddresses.pickup.line2}</p>
                  )}
                  <p>
                    {validatedAddresses?.pickup.city},{" "}
                    {validatedAddresses?.pickup.province}{" "}
                    {validatedAddresses?.pickup.postalCode}
                  </p>
                </div>

                <div>
                  <h4 className="font-medium">Delivery Address:</h4>
                  <p>{validatedAddresses?.delivery.line1}</p>
                  {validatedAddresses?.delivery.line2 && (
                    <p>{validatedAddresses.delivery.line2}</p>
                  )}
                  <p>
                    {validatedAddresses?.delivery.city},{" "}
                    {validatedAddresses?.delivery.province}{" "}
                    {validatedAddresses?.delivery.postalCode}
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={skipAddressConfirmation}>
              Keep Original
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmAddresses}>
              Confirm Addresses
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className={`space-y-6 px-6 ${isCompleted ? "pointer-events-none opacity-70" : ""}`}
        >
          {isCompleted && (
            <div className="absolute top-4 right-4 z-10">
              <div className="bg-green-100 text-green-600 px-4 py-2 rounded-full flex items-center gap-2">
                <Check className="h-4 w-4" />
                Order Created
              </div>
            </div>
          )}

          {/* Sender and Recipient Cards */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Sender Card */}
            <Card className=" border-2 border-l-8 ">
              <CardHeader>
                <CardTitle>Sender Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                <FormField
                  control={form.control}
                  name="senderName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="senderPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Phone</FormLabel>
                      <FormControl>
                        <PhoneInputShadcn
                          {...field}
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? "")}
                          defaultCountry="CA"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <FormLabel>Pickup Address</FormLabel>
                  <div className="mt-2">
                    <AutocompleteAddress
                      onSelectedAddress={(address) => {
                        if (address) {
                          form.setValue("pickupAddress", {
                            line1: address.addressComponents.line1,
                            line2: address.addressComponents.line2 || "",
                            city: address.addressComponents.city,
                            province: address.addressComponents.state,
                            postalCode: address.addressComponents.postalCode,
                            country: address.addressComponents.country,
                            location: address.location,
                          });
                        }
                      }}
                      initialValue={order.senderAddress}
                      showEditFormByDefault={true}
                    />
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="pickupNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Notes</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Add any special instructions for pickup"
                          disabled={isCompleted}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Recipient Card */}
            <Card className=" border-2 border-l-8 ">
              <CardHeader>
                <CardTitle>Recipient Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                <FormField
                  control={form.control}
                  name="recipientName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Recipient Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="recipientPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Recipient Phone</FormLabel>
                      <FormControl>
                        <PhoneInputShadcn
                          {...field}
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? "")}
                          defaultCountry="CA"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <FormLabel>Delivery Address</FormLabel>
                  <div className="mt-2">
                    <AutocompleteAddress
                      onSelectedAddress={(address) => {
                        if (address) {
                          form.setValue("deliveryAddress", {
                            line1: address.addressComponents.line1,
                            line2: address.addressComponents.line2 || "",
                            city: address.addressComponents.city,
                            province: address.addressComponents.state,
                            postalCode: address.addressComponents.postalCode,
                            country: address.addressComponents.country,
                            location: address.location,
                          });
                        }
                      }}
                      initialValue={order.recipientAddress}
                      showEditFormByDefault={true}
                    />
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="deliveryNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delivery Notes</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Add any special instructions for delivery"
                          disabled={isCompleted}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>

          {/* Parcels Card */}
          <Card>
            <CardHeader>
              <CardTitle>Parcels</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dimensions (L×W×H cm)</TableHead>
                    <TableHead>Weight (kg)</TableHead>
                    <TableHead>Special Handling</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.watch("parcels").map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Input
                            type="number"
                            {...form.register(
                              `parcels.${index}.dimensions.length`,
                              {
                                valueAsNumber: true,
                              }
                            )}
                            className="w-20"
                            placeholder="L"
                          />
                          <Input
                            type="number"
                            {...form.register(
                              `parcels.${index}.dimensions.width`,
                              {
                                valueAsNumber: true,
                              }
                            )}
                            className="w-20"
                            placeholder="W"
                          />
                          <Input
                            type="number"
                            {...form.register(
                              `parcels.${index}.dimensions.height`,
                              {
                                valueAsNumber: true,
                              }
                            )}
                            className="w-20"
                            placeholder="H"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          {...form.register(`parcels.${index}.weight`, {
                            valueAsNumber: true,
                          })}
                          className="w-24"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-4">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={form.watch(`parcels.${index}.isFragile`)}
                              onCheckedChange={(checked) =>
                                form.setValue(
                                  `parcels.${index}.isFragile`,
                                  !!checked
                                )
                              }
                            />
                            <span className="text-sm">Fragile</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={form.watch(
                                `parcels.${index}.requiresCooling`
                              )}
                              onCheckedChange={(checked) =>
                                form.setValue(
                                  `parcels.${index}.requiresCooling`,
                                  !!checked
                                )
                              }
                            />
                            <span className="text-sm">Cooling</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {form.watch("parcels").length > 1 && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              const parcels = form.getValues("parcels");
                              form.setValue(
                                "parcels",
                                parcels.filter((_, i) => i !== index)
                              );
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
            <CardFooter className="justify-center border-t p-4">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="gap-1"
                onClick={() => {
                  const parcels = form.getValues("parcels");
                  form.setValue("parcels", [
                    ...parcels,
                    {
                      weight: 2,
                      dimensions: { length: 30, width: 30, height: 30 },
                      isFragile: false,
                      requiresCooling: false,
                    },
                  ]);
                }}
              >
                <PlusCircle className="h-3.5 w-3.5" />
                Add Parcel
              </Button>
            </CardFooter>
          </Card>

          {/* Pricing Options and Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Map Column */}
            <Card className="h-[600px]">
              <CardContent className="p-0 h-full">
                {!!form.watch("pickupAddress.location")?.lat &&
                !!form.watch("deliveryAddress.location")?.lat ? (
                  <ShipmentMap
                    pickupLocation={form.watch("pickupAddress.location")}
                    deliveryLocation={form.watch("deliveryAddress.location")}
                  />
                ) : (
                  <div className="w-full h-full p-4">
                    <Skeleton className="w-full h-full rounded-lg" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pricing Options Column */}
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Delivery Options</CardTitle>
              </CardHeader>
              <CardContent>
                {pricingOptions.length > 0 ? (
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <div className="space-y-4">
                        {pricingOptions.map((option) => (
                          <div
                            key={option.speedType}
                            className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                              field.value === option.speedType
                                ? "border-blue-500 bg-blue-50/50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            onClick={() =>
                              field.onChange(
                                option.speedType as
                                  | "STANDARD"
                                  | "EXPRESS"
                                  | "SCHEDULED"
                                  | "SAME_DAY"
                              )
                            }
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium text-lg">
                                  {option.speedType.replace("_", " ")}
                                </h4>
                                <p className="text-sm text-gray-500 mt-1">
                                  Estimated Delivery Time:{" "}
                                  {typeof option.estimatedDeliveryTime ===
                                  "object"
                                    ? `${option.estimatedDeliveryTime.minHours}-${option.estimatedDeliveryTime.maxHours} hours`
                                    : option.estimatedDeliveryTime}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium">
                                  ${option.price.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                ) : isPricingLoading ? (
                  // Skeleton for pricing options loading
                  <div className="space-y-4">
                    <Skeleton className="h-24 w-full rounded-xl" />
                    <Skeleton className="h-24 w-full rounded-xl" />
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">
                      No delivery options available
                    </p>
                    {shippingOptionsError && (
                      <p className="text-sm text-amber-600 mt-2">
                        {shippingOptionsError}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </form>
      </Form>
    </>
  );
}
